/// <reference types="@types/google.accounts" />
import { AfterViewInit, Component, ElementRef, OnInit, ViewChild, OnDestroy } from '@angular/core'
import {
    FormControl,
    Validators,
    FormGroup,
    FormControlStatus,
} from '@angular/forms'
import { HttpClient, HttpErrorResponse } from '@angular/common/http'
import { Router, ActivatedRoute, ParamMap } from '@angular/router'
import { environment } from '../../environments/environment'
import { CallerService } from '../services/caller.service'
import { GoogleSignInService } from '../services/google-signin.service'
import { AnalyticsService } from '../services/analytics.service'
import { setCorpMemberProfileData } from '../ngrx-store/actions/corp-member.actions'
import { Subscription } from 'rxjs'
import * as Sentry from '@sentry/angular';

@Component({
    selector: 'app-signup',
    templateUrl: './signup.component.html',
    styleUrls: ['./signup.component.css'],
})
export class SignupComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('passwordInput') passwordInput!: ElementRef

    @ViewChild('googleSignInButton', { read: ElementRef<HTMLDivElement> })
    GoogleSignInButton!: ElementRef<HTMLDivElement>;

    // 'https://www.googleapis.com/auth/userinfo.profile https://www.googleapis.com/auth/userinfo.email'
    GOOGLE_SCOPE = 'https://www.googleapis.com/auth/drive.file email'; // email profile openid

    // initialize the code client
    codeClient: google.accounts.oauth2.CodeClient | null = null

    private subscriptions: Subscription = new Subscription()

    constructor(
        private route: ActivatedRoute,
        private callerService: CallerService,
        private router: Router,
        private googleSignInService: GoogleSignInService,
        private analyticsService: AnalyticsService,
    ) {
        // Initialize the code client when Google API is available
        this.initializeCodeClient();
    }

    selected_state_lgas: string[] = []

    passwordInputIcon: string = 'visibility'

    isSigningUp: boolean = false

    // regex for email validation
    email_regex: RegExp = /^[\w\-.+]+@([\w-]+\.)+[\w-]{2,4}$/i

    signupForm = new FormGroup({
        email: new FormControl('', [Validators.required, Validators.pattern(this.email_regex)]),
        state_code: new FormControl('', [
            Validators.required, // comment out later for prospective corp member
            Validators.pattern(this.callerService.state_code_regex),
        ]),
        password: new FormControl('', [
            Validators.required,
            Validators.minLength(6),
        ]),
        // first_name: new FormControl('', [
        //   // Validators.required,
        //   // Validators.minLength(2)
        // ]),
        // middle_name: new FormControl('', [
        //   Validators.minLength(2)
        // ]),
        // last_name: new FormControl('', [
        //   // Validators.required,
        //   // Validators.minLength(2)
        // ]),
        lga: new FormControl({ value: '', disabled: true }, [
            Validators.required,
        ]),
        // is_prospective_corp_member: new FormControl(false), // un-comment later for prospective corp member
        ref_code: new FormControl(''),
    })

    ngOnInit() {
        // call the /hi endpoint to wake up the server
        this.callerService.sayHi().subscribe({
          error: (error) => {
            console.error('Failed to wake up the server:', error);
          }
        })

        const ref_code = this.route.snapshot.queryParamMap.get('ref')
        if (ref_code) {
            this.signupForm.get('ref_code')?.setValue(ref_code)
        }
        
        this.signupForm
            .get('state_code')
            ?.statusChanges.subscribe((statusValidity: FormControlStatus) => {
                console.log('statusValidity', statusValidity)
                if (statusValidity === 'VALID') {
                    // enable LGA input
                    this.signupForm.get('lga')?.enable()

                    // update the lga options based on the state in the state code
                    const state_short = this.signupForm
                        .get('state_code')
                        ?.value?.substring(0, 2)
                        ?.toUpperCase()
                    // get the index of the short state code from the state_code_short array
                    if (typeof state_short === 'string') {
                        const _index =
                            this.callerService.states_short.indexOf(state_short)
                        // get the long name from the states long names array
                        const state_long_name =
                            this.callerService.states_long[_index]

                        // then fetch the LGAs using the state_long_name as key for the LGA object
                        if (
                            state_long_name in
                            this.callerService.states_long_lgas
                        ) {
                            // @ts-ignore
                            this.selected_state_lgas =
                                // @ts-ignore
                                this.callerService.states_long_lgas?.[
                                    state_long_name
                                ]
                        }
                    }
                } else {
                    // disable the LGA input, and empty the options.
                    this.signupForm.get('lga')?.disable()
                    this.selected_state_lgas = []
                }
            })

        // Watch for changes in is_prospective_corp_member to conditionally require state_code
        // this.signupForm
        //     .get('is_prospective_corp_member')
        //     ?.valueChanges.subscribe((isProspective: boolean | null) => {
        //         const stateCodeControl = this.signupForm.get('state_code')
        //         const lgaControl = this.signupForm.get('lga')
                
        //         if (stateCodeControl) {
        //             if (!isProspective) {
        //                 // If not prospective corp member, make state_code required and enable it
        //                 stateCodeControl.setValidators([
        //                     Validators.required,
        //                     Validators.pattern(this.callerService.state_code_regex)
        //                 ])
        //                 stateCodeControl.enable()
        //             } else {
        //                 // If prospective corp member, remove required validator and disable it
        //                 stateCodeControl.setValidators([
        //                     Validators.pattern(this.callerService.state_code_regex)
        //                 ])
        //                 stateCodeControl.disable()
        //             }
        //             stateCodeControl.updateValueAndValidity()
        //         }
                
        //         if (lgaControl) {
        //             if (!isProspective) {
        //                 // If not prospective corp member, enable LGA (it will be enabled by state_code validation)
        //                 // Note: LGA is already handled by the state_code statusChanges subscription
        //             } else {
        //                 // If prospective corp member, disable LGA
        //                 lgaControl.disable()
        //                 this.selected_state_lgas = []
        //             }
        //         }
        //     })

    }

    ngAfterViewInit() {
        // Initialize Google Sign-In button after the view has been initialized
        this.initializeGoogleSignIn();
    }

    private initializeCodeClient() {
        const initSub = this.googleSignInService.initCodeClient({
            client_id: environment.GOOGLE_CLIENT_ID,
            scope: this.GOOGLE_SCOPE,
            callback: (response: google.accounts.oauth2.CodeResponse) => {
                // send the response to your backend for verification and to get user info
            },
        }).subscribe({
            next: (codeClient) => {
                this.codeClient = codeClient;
            },
            error: (error) => {
                console.warn('Failed to initialize Google OAuth2 Code Client:', error);
                // This is optional functionality, so we don't show an error to the user
            }
        });
        this.subscriptions.add(initSub);
    }

    signUp() {
        if (this.signupForm.valid) {
            this.isSigningUp = true
            this.callerService.signUp(this.signupForm.value).subscribe(
                (res: any) => {
                    // @todo: make sure we get a response, for now it's nothing. once it's success, we good

                    localStorage.setItem(
                        CallerService.LOCAL_STORAGE_DATA_KEY,
                        JSON.stringify(res.body.result),
                    )

                    if (res.body.token) {
                        sessionStorage.setItem(CallerService.SESSION_STORAGE_DATA_KEY, res.body.token);
                    }
                    // SocketEffects.connectSocketsOnProfileSet$ reacts to this dispatch and connects
                    // the sockets — see that effect for why this is centralized there now.
                    this.callerService.store.dispatch(setCorpMemberProfileData({data: res.body.result}))

                    this.analyticsService.signupComplete('email')

                    const email = this.signupForm.get('email')?.value
                    if (email) {
                        const storeCredentialSub = this.googleSignInService.storeCredential({
                            id: email,
                            password: this.signupForm.get('password')?.value ?? '',
                        }, () => {
                            console.log('Credential stored successfully');
                        }).subscribe({
                            error: (error) => {
                                console.log('error storing credential', error)
                            }
                        });
                        this.subscriptions.add(storeCredentialSub);
                    }

                    this.isSigningUp = false

                    this.callerService.redirectAfterAuth()
                },
                (err: HttpErrorResponse) => {

                    this.isSigningUp = false
                    this.callerService.showNotification(
                        err?.error?.message ?? 'An error occurred',
                    )

                    // capture the error on sentry.
                    Sentry.captureException(err);
                },
                () => {},
            )
        } else {
            // console.log('sign up form errors', this.signupForm)
        }
    }

    togglePasswordInputIcon(): void {
        if (this.passwordInputIcon == 'visibility') {
            this.passwordInputIcon = 'visibility_off'
            this.passwordInput.nativeElement.type = 'text'
        } else {
            this.passwordInputIcon = 'visibility'
            this.passwordInput.nativeElement.type = 'password'
        }
    }

    initializeGoogleSignIn() {
        if (!this.GoogleSignInButton?.nativeElement) {
            console.warn('Google Sign-In button element not found');
            return;
        }

        // Initialize Google Sign-In
        const initSub = this.googleSignInService.initializeGoogleSignIn({
            client_id: environment.GOOGLE_CLIENT_ID,
            callback: this.handleCredentialResponse.bind(this),
        }).subscribe({
            next: () => {
                // After initialization, render the button
                const renderSub = this.googleSignInService.renderButton(
                    this.GoogleSignInButton.nativeElement,
                    { type: 'standard', theme: 'outline', size: 'large', text: 'continue_with', width: 256 }
                ).subscribe({
                    error: (error) => {
                        console.error('Failed to render Google Sign-In button:', error);
                        this.callerService.showNotification(
                            'Failed to load Google Sign-In. Please refresh the page.',
                            5000,
                            'Close'
                        );
                    }
                });
                this.subscriptions.add(renderSub);
            },
            error: (error) => {
                console.error('Failed to initialize Google Sign-In:', error);
                this.callerService.showNotification(
                    'Failed to load Google Sign-In. Please refresh the page.',
                    5000,
                    'Close'
                );
            }
        });
        this.subscriptions.add(initSub);

        // For one-tap sign-in prompt:
        // this.googleSignInService.loadGoogleSignInScript().subscribe(google => {
        //     google.accounts.id.prompt();
        // });
    }

    handleCredentialResponse(response: any) {
        // This function is called when the user successfully signs in with Google
        // The 'response.credential' contains the ID token (JWT)
        const idToken = response.credential;

        // **Important:** Send this ID token to your backend server for verification.
        // Do NOT rely solely on client-side verification for security.
        // Your backend should verify the token's integrity and validity.

        this.callerService.corpMemberGoogleSignUp({
            google_token: idToken,
            ref_code: this.signupForm.get('ref_code')?.value,
        }).subscribe({
            next: (res: any) => {
                // Handle successful sign-up, e.g., store user data, navigate to a different page
                localStorage.setItem(
                    CallerService.LOCAL_STORAGE_DATA_KEY,
                    JSON.stringify(res.body.result),
                );
                if (res.body.token) {
                    sessionStorage.setItem(CallerService.SESSION_STORAGE_DATA_KEY, res.body.token);
                }
                // SocketEffects.connectSocketsOnProfileSet$ reacts to this dispatch and connects
                // the sockets — see that effect for why this is centralized there now.
                this.callerService.store.dispatch(setCorpMemberProfileData({data: res.body.result}))

                this.analyticsService.signupComplete('google')
                // Same as email sign-up: the onboarding checklist asks for what Google doesn't give us
                // (the state code and LGA), so there's no need to send them to their profile first.
                this.callerService.redirectAfterAuth()
            },
            error: (err: HttpErrorResponse) => {
                console.error('Google Sign-Up failed:', err);
                // Handle error, e.g., show a notification to the user
                this.callerService.showNotification(
                    err?.error?.message ?? 'An error occurred during Google sign-up',
                );
            }
        });


    }

    // Uncomment if we need to request access to their google drive and save uploads there
    // askForGoogleAuth() {
    //     this.codeClient?.requestCode();
    // }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }
}
